<script lang="ts">
  import { LAYOUTS } from '@multiview/shared';
  import { engine, PIP_OPACITIES, PIP_SIZES } from '../lib/player/engine.svelte.ts';
  import { SOUND_VOLUMES, ticker, TICKER_SIZES, TICKER_SPEEDS } from '../lib/ticker.svelte.ts';
  import { playSound } from '../lib/sounds.ts';

  /**
   * The everyday controls, laid over the bottom of the picture so the game stays
   * in view. The full menu is one step further, for the rarely-used settings.
   */
  interface Props { onClose: () => void; onMore: () => void; onInfo: () => void }
  const { onClose, onMore, onInfo }: Props = $props();

  const AUTO_MODES = [
    { value: 'off', label: 'Aus' },
    { value: 'interval', label: 'Nach Zeit' },
    { value: 'excitement', label: 'Bei Jubel' },
    { value: 'both', label: 'Zeit + Jubel' },
  ] as const;

  const INTERVALS = [
    { seconds: 30, label: '30 s' },
    { seconds: 60, label: '1 min' },
    { seconds: 120, label: '2 min' },
    { seconds: 300, label: '5 min' },
    { seconds: 600, label: '10 min' },
    { seconds: 900, label: '15 min' },
    { seconds: 1800, label: '30 min' },
  ] as const;

  /** Unused for this long, the bar gets out of the way of the picture. */
  const IDLE_MS = 12_000;

  const visible = $derived(engine.visibleAngles);
  const autoModes = $derived(
    engine.audioDisabledReason ? AUTO_MODES.filter((m) => m.value === 'off' || m.value === 'interval') : AUTO_MODES,
  );
  const timed = $derived(engine.autoMode === 'interval' || engine.autoMode === 'both');
  const hasFixture = $derived(engine.angles.some((a) => a.fixtureExternalId));

  let now = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(id);
  });
  const countdown = $derived.by(() => {
    if (engine.autoNextAt === null) return null;
    const left = Math.max(0, Math.round((engine.autoNextAt - now) / 1000));
    return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  });

  $effect(() => {
    let timer = setTimeout(onClose, IDLE_MS);
    const poke = () => {
      clearTimeout(timer);
      timer = setTimeout(onClose, IDLE_MS);
    };
    const events = ['keydown', 'pointermove', 'pointerdown', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, poke, { passive: true });
    return () => {
      clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, poke);
    };
  });
</script>

<div class="bar" role="toolbar" aria-label="Schnellsteuerung">
  <div class="head">
    <span class="now">
      <span class="faint">Hauptbild</span> <b>{engine.mainAngle?.label ?? '—'}</b>
      {#if countdown}
        <span class="faint">· Wechsel zu {engine.nextAutoAngle?.label ?? '—'} in</span>
        <span class="mono">{countdown}</span>
      {/if}
    </span>
    <span class="actions">
      <button data-focusable onclick={onInfo}>Spielinfos</button>
      <button data-focusable onclick={onMore}>Mehr …</button>
      <button data-focusable onclick={onClose}>✕</button>
    </span>
  </div>

  <div class="rows">
    {#if visible.length > 1}
      <div class="row">
        <span class="label faint">Bild</span>
        <div class="chips">
          {#each visible as angle, i (angle.id)}
            <button
              data-focusable
              data-focus-default={angle.id === engine.mainAngleId ? '' : undefined}
              class:active={angle.id === engine.mainAngleId}
              onclick={() => engine.setMain(angle.id)}
            ><span class="key">{i + 1}</span> {angle.label}</button>
          {/each}
        </div>
      </div>
    {/if}

    {#if engine.availableLayouts.length > 1}
      <div class="row">
        <span class="label faint">Layout</span>
        <div class="chips">
          {#each engine.availableLayouts as id (id)}
            <button
              data-focusable
              class:active={engine.effectiveLayout === id}
              onclick={() => engine.setLayout(id)}
            >{LAYOUTS[id].label}</button>
          {/each}
        </div>
      </div>
    {/if}

    {#if ticker.competitions.length > 0}
      <div class="row">
        <span class="label faint">Laufband</span>
        <div class="chips">
          <button
            data-focusable
            class="toggle"
            class:active={ticker.on}
            aria-pressed={ticker.on}
            onclick={() => ticker.setOn(!ticker.on)}
          >{ticker.on ? '✓ An' : '○ Aus'}</button>
          <button
            data-focusable
            class="toggle"
            class:active={ticker.goals}
            aria-pressed={ticker.goals}
            onclick={() => ticker.setGoals(!ticker.goals)}
          >{ticker.goals ? '✓' : '○'} Tor-Einblendung</button>
          <button
            data-focusable
            class="toggle"
            class:active={ticker.cards}
            aria-pressed={ticker.cards}
            onclick={() => ticker.setCards(!ticker.cards)}
          >{ticker.cards ? '✓' : '○'} Rot & Elfmeter</button>
          <button
            data-focusable
            class="toggle"
            class:active={ticker.yellow}
            aria-pressed={ticker.yellow}
            onclick={() => ticker.setYellow(!ticker.yellow)}
          >{ticker.yellow ? '✓' : '○'} Gelbe Karten</button>
          {#if ticker.on || ticker.announcing}
            {#each ticker.competitions as name (name)}
              <button
                data-focusable
                class:active={ticker.selected.includes(name)}
                onclick={() => ticker.toggleCompetition(name)}
              >{name}</button>
            {/each}
          {/if}
        </div>
      </div>
    {/if}

    {#if ticker.announcing}
      <div class="row">
        <span class="label faint">Töne</span>
        <div class="chips">
          {#each SOUND_VOLUMES as volume (volume.value)}
            <button
              data-focusable
              class:active={ticker.style.sound === volume.value}
              onclick={() => { ticker.setStyle({ sound: volume.value }); playSound('whistleShort', volume.value); }}
            >{volume.label}</button>
          {/each}
        </div>
      </div>
    {/if}

    {#if ticker.on && ticker.visible.length > 0}
      <div class="row">
        <span class="label faint">Laufband-Stil</span>
        <div class="chips">
          <button
            data-focusable
            class="toggle"
            class:active={ticker.style.logos}
            aria-pressed={ticker.style.logos}
            onclick={() => ticker.setStyle({ logos: !ticker.style.logos })}
          >{ticker.style.logos ? '✓' : '○'} Vereinslogos</button>
          {#each TICKER_SIZES as size (size.value)}
            <button
              data-focusable
              class:active={ticker.style.size === size.value}
              onclick={() => ticker.setStyle({ size: size.value })}
            >{size.label}</button>
          {/each}
        </div>
      </div>
      <div class="row">
        <span class="label faint">Laufband-Tempo</span>
        <div class="chips">
          {#each TICKER_SPEEDS as speed (speed.value)}
            <button
              data-focusable
              class:active={ticker.style.speed === speed.value}
              onclick={() => ticker.setStyle({ speed: speed.value })}
            >{speed.label}</button>
          {/each}
        </div>
      </div>
    {/if}

    {#if LAYOUTS[engine.effectiveLayout].pip}
      <div class="row">
        <span class="label faint">Klein</span>
        <div class="chips">
          {#each PIP_SIZES as size (size.value)}
            <button
              data-focusable
              class:active={engine.pip.size === size.value}
              onclick={() => engine.setPip({ size: size.value })}
            >{size.label}</button>
          {/each}
        </div>
      </div>
      <div class="row">
        <span class="label faint">Deckkraft</span>
        <div class="chips">
          {#each PIP_OPACITIES as opacity (opacity)}
            <button
              data-focusable
              class:active={engine.pip.opacity === opacity}
              onclick={() => engine.setPip({ opacity })}
            >{Math.round(opacity * 100)} %</button>
          {/each}
        </div>
      </div>
    {/if}

    {#if visible.length > 1}
      <div class="row">
        <span class="label faint">Auto</span>
        <div class="chips">
          {#each autoModes as mode (mode.value)}
            <button
              data-focusable
              class:active={engine.autoMode === mode.value}
              onclick={() => engine.setAutoMode(mode.value)}
            >{mode.label}</button>
          {/each}
          {#if hasFixture}
            <button
              data-focusable
              class="toggle"
              class:active={engine.goalSwitching}
              aria-pressed={engine.goalSwitching}
              onclick={() => engine.setGoalSwitching(!engine.goalSwitching)}
            >{engine.goalSwitching ? '✓' : '○'} Bei Tor</button>
          {/if}
        </div>
      </div>

      {#if timed}
        <div class="row">
          <span class="label faint">Alle</span>
          <div class="chips">
            {#each INTERVALS as option (option.seconds)}
              <button
                data-focusable
                class:active={engine.autoIntervalSeconds === option.seconds}
                onclick={() => engine.setAutoInterval(option.seconds)}
              >{option.label}</button>
            {/each}
            <button data-focusable class="now-btn" onclick={() => engine.autoSwitchNow()}>⇄ Jetzt wechseln</button>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .bar {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    z-index: 25;
    padding: calc(var(--pad) * 2) var(--safe-inset) var(--safe-inset);
    background: linear-gradient(to top, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.8) 70%, transparent 100%);
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    animation: rise 0.18s ease-out;
  }

  @keyframes rise {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: none; opacity: 1; }
  }

  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--gap); }
  .now { font-size: var(--font-sm); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .actions { display: flex; gap: var(--gap); flex-shrink: 0; }

  .rows { display: flex; flex-direction: column; gap: var(--gap); }
  .row { display: flex; align-items: center; gap: var(--gap); min-width: 0; }
  .label { flex: 0 0 4.2em; font-size: var(--font-sm); text-transform: uppercase; letter-spacing: 0.04em; }

  /* One line per row; on a narrow screen it scrolls sideways instead of
     stacking up over the picture. */
  .chips {
    display: flex;
    gap: var(--gap);
    overflow-x: auto;
    scrollbar-width: none;
    padding: 4px 2px;
    min-width: 0;
  }
  .chips::-webkit-scrollbar { display: none; }

  button { white-space: nowrap; font-size: var(--font-sm); background: rgba(20, 26, 36, 0.85); }
  button.active { border-color: var(--accent); color: var(--accent); }
  .now-btn { margin-left: var(--gap); }

  .key {
    display: inline-block;
    min-width: 1.4em;
    text-align: center;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.12);
    font-variant-numeric: tabular-nums;
  }

  :global([data-platform="mobile"]) .label { flex-basis: 3.4em; }
</style>
